import { Plus, Trash2 } from 'lucide-react';
import { Button, Spinner } from '@heroui/react';
import type { AiConversationSummary } from '../../api/ai';
import {
  ASSISTANT_CONVERSATIONS_LABEL,
  ASSISTANT_NEW_CHAT_LABEL,
  conversationTitle,
  formatConversationTimestamp,
} from './assistant-constants';

interface AssistantConversationListProps {
  conversations: AiConversationSummary[];
  selectedId: string | null;
  loading?: boolean;
  onSelect: (conversationId: string) => void;
  onNewChat: () => void;
  onDelete: (conversationId: string) => void;
}

export function AssistantConversationList({
  conversations,
  selectedId,
  loading = false,
  onSelect,
  onNewChat,
  onDelete,
}: AssistantConversationListProps) {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted">
          {ASSISTANT_CONVERSATIONS_LABEL}
        </p>
        <Button size="sm" variant="secondary" onPress={onNewChat}>
          <Plus size={14} />
          {ASSISTANT_NEW_CHAT_LABEL}
        </Button>
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center gap-2 px-1 py-3 text-sm text-muted">
            <Spinner size="sm" />
            Carregando…
          </div>
        ) : conversations.length === 0 ? (
          <p className="px-1 py-3 text-sm text-muted">Nenhuma conversa ainda.</p>
        ) : (
          conversations.map((conversation) => {
            const selected = conversation.id === selectedId;
            const title = conversationTitle(conversation.title, conversation.preview);
            return (
              <div
                key={conversation.id}
                className={`flex items-start gap-1 rounded-xl px-1 py-1 ${
                  selected ? 'bg-surface-secondary' : ''
                }`}
              >
                <Button
                  variant="ghost"
                  className="h-auto min-w-0 flex-1 items-start justify-start whitespace-normal px-2 py-2 text-left"
                  onPress={() => onSelect(conversation.id)}
                >
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate text-sm font-medium text-foreground">{title}</span>
                    {conversation.preview ? (
                      <span className="mt-0.5 line-clamp-2 text-xs font-normal text-muted">
                        {conversation.preview}
                      </span>
                    ) : null}
                    <span className="mt-1 text-[11px] font-normal text-muted">
                      {formatConversationTimestamp(conversation.lastMessageAt)}
                    </span>
                  </span>
                </Button>
                <Button
                  isIconOnly
                  size="sm"
                  variant="ghost"
                  aria-label="Excluir conversa"
                  onPress={() => onDelete(conversation.id)}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
